// ABOUTME: Switches this browser from the old gnowbe-web app to the new gnowbe-frontend app
// ABOUTME: Also sends the Gnowbe desktop app back to the new app, because this app cannot sign in there

/**
 * Unregisters every service worker for this origin and drops all Cache Storage
 * buckets. Best effort only — every failure is swallowed so the app flip is
 * never blocked by cleanup.
 */
async function evictServiceWorkers(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((r) => r.unregister()))
    }
    // Drop image-cache-v* too: entries are keyed by path and matched with
    // `ignoreSearch: true`, so stale bytes can be served for URLs the new app
    // requests with different signatures.
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    // Never block the flip on cleanup failure.
  }
}

/**
 * Sets app_version=new (2 years) and reloads the SAME url. CloudFront reads this cookie to
 * decide which bundle to serve, so the reload lands on the new app.
 */
export async function switchToNewApp(): Promise<void> {
  // Set cookie app_version=new for 2 years. This goes FIRST — if the eviction
  // below hangs or throws, the flip must still land on the new app.
  const date = new Date()
  date.setTime(date.getTime() + (2 * 365 * 24 * 60 * 60 * 1000))
  document.cookie = `app_version=new; expires=${date.toUTCString()}; path=/`

  // Evict this app's service worker + caches before the reload.
  //
  // A plain reload does NOT release SW control of the document, so without
  // this the new app boots with sw.js still intercepting every cdn1.* request.
  // sw.js re-fetches those with `credentials: 'omit'`, which strips the
  // CloudFront signed cookies the new app authenticates media with → 403 on
  // every program image. unregister() only stops FUTURE documents from being
  // controlled, so it has to happen before the navigation, not after.
  await Promise.race([
    evictServiceWorkers(),
    new Promise((resolve) => setTimeout(resolve, 1500)),
  ])

  window.location.reload()
}

/**
 * True inside the Gnowbe desktop app (gnowbe-desktop, Tauri). The shell injects
 * `window.__GNOWBE_DESKTOP__` before any page script runs; no browser has it.
 */
export function isDesktopShell(): boolean {
  const shell = (window as Window & { __GNOWBE_DESKTOP__?: { version?: unknown } }).__GNOWBE_DESKTOP__
  return !!shell && typeof shell.version === 'string'
}

const DESKTOP_FLIP_KEY = 'gnowbe_desktop_shell_flip_at'
const DESKTOP_FLIP_RETRY_MS = 60 * 1000

/**
 * Whether this boot should send the desktop app back to the new app.
 *
 * The desktop app loads web.gnowbe.com, so an app_version=old cookie (the new app's
 * "Return to old web") serves it this app. This app cannot sign anyone in there: its login
 * page runs on be.gnowbe.com inside the desktop app, while the desktop app opens every
 * api.* url (the Google/SSO connect routes) in the system browser. The provider login then
 * comes back to be.gnowbe.com in that browser, which does not have the login page's
 * session, and the user gets "Invalid request" while the desktop app stays on the login.
 *
 * At most once a minute per tab: if the edge still serves this app after the flip (a
 * blocked cookie write, a duplicate cookie), we boot normally instead of reloading forever.
 * Without sessionStorage we cannot tell, so we do not flip.
 */
export function shouldSendDesktopShellToNewApp(): boolean {
  if (!isDesktopShell()) return false
  try {
    const lastAt = Number(window.sessionStorage.getItem(DESKTOP_FLIP_KEY))
    if (lastAt && Date.now() - lastAt < DESKTOP_FLIP_RETRY_MS) return false
    window.sessionStorage.setItem(DESKTOP_FLIP_KEY, String(Date.now()))
    return true
  } catch {
    return false
  }
}
